/**
 * Copyright 2026 Ziz Academy
 */

import axios from 'axios';
import { User, LoginCredentials, Exam, Question, ExamSubmission, SubmitExam, Evaluation, ExamCategory } from '../types';

const API_BASE_URL = '/api';

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Centralized Axios error to meaningful message extractor
const extractErrorMessage = (error: unknown): string => {
    // AxiosError has response with server message
    const anyErr = error as any;
    if (anyErr && anyErr.response) {
        const data = anyErr.response.data;
        if (data) {
            if (typeof data === 'string') {
                return data;
            }
            if (data.message && typeof data.message === 'string') {
                return data.message;
            }
            if (data.error && typeof data.error === 'string') {
                return data.error;
            }
            if (Array.isArray(data.errors) && data.errors.length > 0) {
                const first = data.errors[0];
                if (typeof first === 'string') return first;
                if (first && typeof first.message === 'string') return first.message;
            }
        }
        // Fallback to status text or generic
        if (anyErr.response.statusText) return anyErr.response.statusText;
    }
    // Non-Axios or unexpected error shape
    if (anyErr && anyErr.message) return anyErr.message;
    return 'Request failed. Please try again.';
};

// User API
export const userApi = {
    register: async (user: User): Promise<User> => {
        try {
            const response = await api.post('/users/register', user);
            return response.data;
        } catch (error) {
            throw new Error(extractErrorMessage(error));
        }
    },

    login: async (credentials: LoginCredentials): Promise<User> => {
        try {
            const response = await api.post('/users/login', credentials);
            return response.data;
        } catch (error) {
            throw new Error(extractErrorMessage(error));
        }
    },

    getById: async (id: number): Promise<User> => {
        const response = await api.get(`/users/${id}`);
        return response.data;
    },

    getAll: async (): Promise<User[]> => {
        const response = await api.get('/users');
        return response.data;
    }
};

// Exam API
export const examApi = {
    create: async (exam: Exam, teacherId: number): Promise<Exam> => {
        const response = await api.post('/exams', exam, { 
            headers: { 'X-User-Id': teacherId.toString() } 
        });
        return response.data;
    },

    // Draft workflow
    createDraft: async (exam: Partial<Exam>, teacherId: number): Promise<Exam> => {
        const response = await api.post('/exams/draft', exam, {
            headers: { 'X-User-Id': teacherId.toString() }
        });
        return response.data;
    },

    updateDraft: async (id: number, exam: Partial<Exam>, teacherId: number): Promise<Exam> => {
        const response = await api.put(`/exams/${id}/draft`, exam, {
            headers: { 'X-User-Id': teacherId.toString() }
        });
        return response.data;
    },

    publish: async (id: number, teacherId: number): Promise<Exam> => {
        const response = await api.post(`/exams/${id}/publish`, {}, {
            headers: { 'X-User-Id': teacherId.toString() }
        });
        return response.data;
    },

    getById: async (id: number): Promise<Exam> => {
        const response = await api.get(`/exams/${id}`);
        return response.data;
    },

    getAll: async (): Promise<Exam[]> => {
        const response = await api.get('/exams');
        return response.data;
    },

    getActive: async (): Promise<Exam[]> => {
        const response = await api.get('/exams/active');
        return response.data;
    },

    getByTeacher: async (teacherId: number): Promise<Exam[]> => {
        const response = await api.get(`/exams/teacher/${teacherId}`);
        return response.data;
    },

    update: async (id: number, exam: Exam): Promise<Exam> => {
        const response = await api.put(`/exams/${id}`, exam);
        return response.data;
    },

    delete: async (id: number): Promise<void> => {
        await api.delete(`/exams/${id}`);
    },

    addQuestion: async (examId: number, question: Question): Promise<Exam> => {
        const response = await api.post(`/exams/${examId}/questions`, question);
        return response.data;
    },

    updateQuestion: async (questionId: number, question: Question): Promise<Question> => {
        const response = await api.put(`/exams/questions/${questionId}`, question);
        return response.data;
    },

    deleteQuestion: async (questionId: number): Promise<void> => {
        await api.delete(`/exams/questions/${questionId}`);
    },

    // Assignments and admin endpoints
    assignExam: async (assignment: { examId: number; studentIds: number[] }): Promise<void> => {
        await api.post(`/exams/assign`, assignment);
    },

    getAssignedExams: async (studentId: number): Promise<Exam[]> => {
        const response = await api.get(`/exams/student/${studentId}/assigned`);
        return response.data;
    },

    getAssignmentsByStudent: async (studentId: number): Promise<import('../types').ExamAssignment[]> => {
        const response = await api.get(`/exams/student/${studentId}/assignments`);
        return response.data;
    },

    getAssignedStudents: async (examId: number): Promise<User[]> => {
        const response = await api.get(`/exams/${examId}/assignments`);
        return response.data;
    },

    getAllExamsForAdmin: async (actingUserId: number, institutionId?: number): Promise<Exam[]> => {
        const response = await api.get(`/exams/admin/all`, {
            headers: { 'X-User-Id': actingUserId.toString() },
            params: institutionId && institutionId > 0 ? { institutionId } : undefined
        });
        return response.data;
    },

    getAllAssignmentsForAdmin: async (actingUserId: number, institutionId?: number): Promise<import('../types').ExamAssignment[]> => {
        const response = await api.get(`/exams/admin/assignments`, {
            headers: { 'X-User-Id': actingUserId.toString() },
            params: institutionId && institutionId > 0 ? { institutionId } : undefined
        });
        return response.data;
    },

    // Unassign exam from a specific student (Teacher/Admin/SuperAdmin)
    unassignExam: async (examId: number, studentId: number, actingUserId: number): Promise<void> => {
        await api.delete(`/exams/${examId}/assignments/${studentId}`, {
            headers: { 'X-User-Id': actingUserId.toString() }
        });
    },

    getAssignmentsByTeacher: async (teacherId: number): Promise<import('../types').ExamAssignment[]> => {
        const response = await api.get(`/exams/teacher/${teacherId}/assignments`);
        return response.data;
    },

    // Draft lists
    getDraftsByTeacher: async (teacherId: number): Promise<Exam[]> => {
        const response = await api.get(`/exams/teacher/${teacherId}/drafts`);
        return response.data;
    },

    getDraftsForAdmin: async (actingUserId: number, institutionId?: number): Promise<Exam[]> => {
        const response = await api.get(`/exams/admin/drafts`, {
            headers: { 'X-User-Id': actingUserId.toString() },
            params: institutionId && institutionId > 0 ? { institutionId } : undefined
        });
        return response.data;
    }
};

// Categories API
export const categoryApi = {
    list: async (): Promise<ExamCategory[]> => {
        const response = await api.get('/categories');
        return response.data;
    },
    create: async (name: string, userId: number): Promise<ExamCategory> => {
        const response = await api.post('/categories', { name }, {
            headers: { 'X-User-Id': userId.toString() }
        });
        return response.data;
    }
};

// Submission API
// Question API (standalone CRUD, backed by per-teacher bank on server)
export const questionApi = {
    listByTeacher: async (teacherId: number): Promise<Question[]> => {
        const response = await api.get(`/questions/teacher/${teacherId}`);
        return response.data;
    },
    // Paged list for teacher with filters
    listByTeacherPaged: async (
        teacherId: number,
        params: { page?: number; size?: number; search?: string; subject?: string; classLevel?: string }
    ): Promise<import('../types').PageResponse<Question>> => {
        const response = await api.get(`/questions/teacher/${teacherId}/paged`, { params });
        return response.data;
    },
    // Admin or Super-Admin list
    listForAdmin: async (actingUserId: number): Promise<Question[]> => {
        const response = await api.get(`/questions/admin`, {
            headers: { 'X-User-Id': actingUserId.toString() }
        });
        return response.data;
    },
    // Paged list for admin with filters
    listForAdminPaged: async (
        actingUserId: number,
        params: { page?: number; size?: number; search?: string; subject?: string; classLevel?: string; institutionId?: number }
    ): Promise<import('../types').PageResponse<Question>> => {
        const { institutionId, ...rest } = params || {} as any;
        const response = await api.get(`/questions/admin/paged`, {
            headers: { 'X-User-Id': actingUserId.toString() },
            params: institutionId && institutionId > 0 ? { ...rest, institutionId } : rest
        });
        return response.data;
    },
    // Paged list for institution/global scope (used when creating exams)
    listForInstitutionPaged: async (
        actingUserId: number,
        params: { page?: number; size?: number; search?: string; subject?: string; classLevel?: string }
    ): Promise<import('../types').PageResponse<Question>> => {
        const response = await api.get(`/questions/institution/paged`, {
            headers: { 'X-User-Id': actingUserId.toString() },
            params
        });
        return response.data;
    },
    create: async (question: Question, teacherId: number): Promise<Question> => {
        const response = await api.post('/questions', question, {
            headers: { 'X-User-Id': teacherId.toString() }
        });
        return response.data;
    },
    update: async (id: number, question: Question): Promise<Question> => {
        const response = await api.put(`/questions/${id}`, question);
        return response.data;
    },
    delete: async (id: number): Promise<void> => {
        await api.delete(`/questions/${id}`);
    }
};

// Submission API
export const submissionApi = {
    submit: async (submission: SubmitExam, studentId: number): Promise<ExamSubmission> => {
        const response = await api.post('/submissions', submission, {
            headers: { 'X-User-Id': studentId.toString() }
        });
        return response.data;
    },

    getById: async (id: number): Promise<ExamSubmission> => {
        const response = await api.get(`/submissions/${id}`);
        return response.data;
    },

    getByStudent: async (studentId: number): Promise<ExamSubmission[]> => {
        const response = await api.get(`/submissions/student/${studentId}`);
        return response.data;
    },

    getByExam: async (examId: number): Promise<ExamSubmission[]> => {
        const response = await api.get(`/submissions/exam/${examId}`);
        return response.data;
    },

    getUnevaluated: async (examId: number): Promise<ExamSubmission[]> => {
        const response = await api.get(`/submissions/exam/${examId}/unevaluated`);
        return response.data;
    },

    evaluate: async (evaluation: Evaluation): Promise<ExamSubmission> => {
        const response = await api.post(`/submissions/evaluate`, evaluation);
        return response.data;
    },

    getAllSubmissionsForAdmin: async (): Promise<ExamSubmission[]> => {
        const response = await api.get(`/submissions/admin/all`);
        return response.data;
    },
};

// Image Upload API
export const imageApi = {
    upload: async (file: File, source: 'button' | 'paste' = 'button'): Promise<{ url: string }> => {
        const form = new FormData();
        form.append('file', file);
        const response = await axios.post('/api/images/upload', form, {
            headers: { 'Content-Type': 'multipart/form-data', 'X-Upload-Source': source },
        });
        return response.data;
    },
    uploadAnswer: async (file: File, source: 'button' | 'paste' = 'button'): Promise<{ url: string }> => {
        const form = new FormData();
        form.append('file', file);
        const response = await axios.post('/api/images/upload-answer', form, {
            headers: { 'Content-Type': 'multipart/form-data', 'X-Upload-Source': source },
        });
        return response.data;
    },
};

// Admin Users API (for Admin and Super Admin)
export const adminUsersApi = {
    list: async (actingUserId: number, institutionId?: number) => {
        const response = await api.get('/admin/users', {
            headers: { 'X-User-Id': actingUserId.toString() },
            params: institutionId && institutionId > 0 ? { institutionId } : undefined
        });
        return response.data as User[];
    },
    create: async (actingUserId: number, user: Partial<User>) => {
        const response = await api.post('/admin/users', user, {
            headers: { 'X-User-Id': actingUserId.toString() }
        });
        return response.data as User;
    },
    update: async (actingUserId: number, id: number, user: Partial<User>) => {
        const response = await api.put(`/admin/users/${id}`, user, {
            headers: { 'X-User-Id': actingUserId.toString() }
        });
        return response.data as User;
    },
    changePassword: async (actingUserId: number, id: number, newPassword: string) => {
        await api.put(`/admin/users/${id}/password`, { newPassword }, {
            headers: { 'X-User-Id': actingUserId.toString() }
        });
    },
    delete: async (actingUserId: number, id: number) => {
        await api.delete(`/admin/users/${id}`, {
            headers: { 'X-User-Id': actingUserId.toString() }
        });
    }
};

// Institutions API (Super Admin only for mutations)
// Exam Folders API
export const examFolderApi = {
    getTree: async (actingUserId: number) => {
        const response = await api.get('/exam-folders/tree', {
            headers: { 'X-User-Id': actingUserId.toString() }
        });
        return response.data as import('../types').ExamFolder[];
    },
    create: async (actingUserId: number, name: string, parentId?: number | null) => {
        const response = await api.post('/exam-folders', { name, parentId: parentId ?? null }, {
            headers: { 'X-User-Id': actingUserId.toString() }
        });
        return response.data as import('../types').ExamFolder;
    },
    moveExam: async (actingUserId: number, examId: number, folderId?: number | null): Promise<void> => {
        await api.post('/exam-folders/move-exam', { examId, folderId: folderId ?? null }, {
            headers: { 'X-User-Id': actingUserId.toString() }
        });
    },
    rename: async (actingUserId: number, folderId: number, name: string) => {
        const response = await api.put(`/exam-folders/${folderId}`, { name }, {
            headers: { 'X-User-Id': actingUserId.toString() }
        });
        return response.data as import('../types').ExamFolder;
    }
};

export const institutionsApi = {
    list: async (): Promise<{ id: number; name: string; createdAt?: string }[]> => {
        const response = await api.get('/institutions');
        return response.data;
    },
    create: async (name: string): Promise<{ id: number; name: string; createdAt?: string }> => {
        const response = await api.post('/institutions', { name });
        return response.data;
    },
    update: async (id: number, name: string): Promise<{ id: number; name: string; createdAt?: string }> => {
        const response = await api.put(`/institutions/${id}`, { name });
        return response.data;
    },
    delete: async (id: number): Promise<void> => {
        await api.delete(`/institutions/${id}`);
    }
};

export default api;